/**
 * Client-side validation for the per-location weekly hours grid + date exceptions
 * (DNKADM-0051 U2). Mirrors the server's shift rules (handoff §5.2, family of
 * §5.6/§5.7) so a well-formed PUT is the norm and an invalid edit never hits the
 * wire.
 *
 * **Shift rules (per available day / per available exception date):**
 *  - every shift has valid `"HH:mm"` `startTime`/`endTime`;
 *  - `start < end`, where an **end of `"00:00"` means end of day (minute 1440)** —
 *    mirrors dink-api's `ShiftBounds` (DNKAPI-0486 / DNKADM-0318): `"00:00"` as an
 *    END is midnight and is valid for any start (`00:00–00:00` is a full day); a
 *    START of `"00:00"` keeps its ordinary meaning. Every other `end <= start` is
 *    rejected as overnight (split across days);
 *  - no overlapping shifts within the same day/date (touching boundaries OK —
 *    adjacent shifts allowed, not auto-merged);
 *  - no exact duplicates;
 *  - an available day/date needs **≥1 shift** (else mark it closed).
 *
 * An unavailable (closed) day/date is always valid and carries no shifts.
 */
import type { HoursShift, WeeklyHoursDay, HoursException } from '@/api/operatingHours';
import { shiftStartMinutes, shiftEndMinutes } from '@/utils/shiftValidation';

/** Per-day error message, keyed by `dayOfWeek` (0=Sun … 6=Sat). */
export type DayErrors = Record<number, string>;
/** Per-exception error message, keyed by `exceptionDate`. */
export type ExceptionErrors = Record<string, string>;

/**
 * Validate a single day/date's shift list against the shift rules. Returns a
 * human error message, or `null` when the list is valid. An empty list is invalid
 * for an available day/date (caller passes only available ones).
 */
export function validateShifts(shifts: HoursShift[]): string | null {
  if (shifts.length === 0) {
    return 'Add at least one shift, or mark this closed.';
  }
  // Parse each shift to [start, end) minute ranges — an end of "00:00" is minute
  // 1440 (end of day, shared parser in `@/utils/shiftValidation`) — then reject
  // bad/overnight ranges.
  const ranges: Array<{ start: number; end: number }> = [];
  for (const shift of shifts) {
    const start = shiftStartMinutes(shift.startTime);
    const end = shiftEndMinutes(shift.endTime);
    if (start === null || end === null) {
      return 'Enter a valid start and end time (HH:mm) for every shift.';
    }
    if (start >= end) {
      return 'Each shift’s start must be before its end (overnight shifts aren’t allowed — an end of 12:00 AM means midnight).';
    }
    ranges.push({ start, end });
  }
  // Sort by start, then check for overlaps / duplicates. Touching boundaries
  // (prev.end === next.start) are adjacent and allowed. Ends are already
  // end-of-day-normalized, so an 18:00–00:00 shift sorts and sweeps as 18:00–24:00.
  const sorted = [...ranges].sort((a, b) => a.start - b.start || a.end - b.end);
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (prev === undefined || cur === undefined) continue;
    if (prev.start === cur.start && prev.end === cur.end) {
      return 'Two shifts are identical — remove the duplicate.';
    }
    if (cur.start < prev.end) {
      return 'Shifts can’t overlap within the same day.';
    }
  }
  return null;
}

/**
 * Validate the 7-day grid. Returns `{ ok: true }` when every day is valid, else the
 * per-day error messages (so each offending row shows its inline error and Save is
 * blocked). A closed (unavailable) day is always valid; an available day's shifts
 * are checked against the shift rules.
 */
export function validateWeeklyHours(
  days: WeeklyHoursDay[],
): { ok: true } | { ok: false; errors: DayErrors } {
  const errors: DayErrors = {};
  for (const day of days) {
    if (!day.isAvailable) continue;
    const message = validateShifts(day.shifts);
    if (message !== null) errors[day.dayOfWeek] = message;
  }
  return Object.keys(errors).length === 0 ? { ok: true } : { ok: false, errors };
}

/**
 * Validate the date-exceptions list. Each available exception's shifts are checked;
 * a duplicate `exceptionDate` is rejected (one entry per date). A closed exception
 * is always valid.
 */
export function validateExceptions(
  exceptions: HoursException[],
): { ok: true } | { ok: false; errors: ExceptionErrors } {
  const errors: ExceptionErrors = {};
  const seen = new Set<string>();
  for (const ex of exceptions) {
    if (seen.has(ex.exceptionDate)) {
      errors[ex.exceptionDate] = 'This date already has an exception — remove the duplicate.';
      continue;
    }
    seen.add(ex.exceptionDate);
    if (!ex.isAvailable) continue;
    const message = validateShifts(ex.shifts);
    if (message !== null) errors[ex.exceptionDate] = message;
  }
  return Object.keys(errors).length === 0 ? { ok: true } : { ok: false, errors };
}
